import { authService } from '../../services/auth';
import { icon } from '../../icons';
import { flagIcon } from '../../icons';
import { i18nService, onLocaleChange, SupportedLocale, t } from '../../services/i18n';
import { pwaInstallService } from '../../services/pwa-install';

export class RcUserMenu extends HTMLElement {
  private static readonly HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
  private disposeLocaleChange?: () => void;
  private disposePwaInstallChange?: () => void;
  private handleDocumentClick = (e: MouseEvent) => {
    if (!this.contains(e.target as Node)) this.closeMenu();
  };
  private handleMouseEnter = () => {
    if (this.supportsHover()) this.openMenu();
  };
  private handleMouseLeave = () => {
    if (this.supportsHover()) this.closeMenu();
  };
  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closeMenu();
    }
  };

  connectedCallback(): void {
    this.render();
    authService.onAuthChange(() => this.render());
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.disposePwaInstallChange = pwaInstallService.subscribe(() => this.render());
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeyDown);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    this.disposePwaInstallChange?.();
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  private render(): void {
    const user = authService.currentUser;
    if (!user) return;

    const initials = this.getInitials(user.name);
    const currentLocale = i18nService.currentLocale;
    const installState = pwaInstallService.installState;
    const isTenantMode = user.accountMode === 'tenant';
    const accountModeHint = t(isTenantMode ? 'userMenu.accountModeTenantHint' : 'userMenu.accountModeLandlordHint');
    const localeOptions = i18nService.supportedLocales
      .map(locale => `
        <button type="button" class="user-menu-locale${locale === currentLocale ? ' active' : ''}" data-locale="${locale}">
          <span class="language-flag" aria-hidden="true">${flagIcon(locale)}</span>
          <span class="user-menu-locale-label">${t(`language.${locale}`)}</span>
          ${locale === currentLocale ? `<span class="user-menu-check" aria-hidden="true">${icon('check', { size: 14, strokeWidth: 2.4 })}</span>` : ''}
        </button>
      `).join('');

    this.innerHTML = `
      <div class="user-menu">
        <button type="button" class="user-menu-trigger" aria-haspopup="true" aria-expanded="false" aria-label="${this.escape(user.name)}">
          <span class="user-avatar">${this.escape(initials)}</span>
          <span class="user-menu-chevron" aria-hidden="true">${icon('chevron-right', { size: 14, strokeWidth: 2.4 })}</span>
        </button>
        <div class="user-menu-dropdown" hidden>
          <a href="/profile/" class="user-menu-header">
            <span class="user-avatar user-avatar--lg">${this.escape(initials)}</span>
            <span class="user-menu-header-copy">
              <span class="user-menu-name">${this.escape(user.name)}</span>
              <span class="user-menu-link-hint">${t('userMenu.profileHint')}</span>
            </span>
          </a>
          <div class="user-menu-divider"></div>
          <a href="/profile/#account-mode-section" class="user-menu-item user-menu-item--mode">
            <span class="user-menu-item-copy">
              <span class="user-menu-item-label">${t('userMenu.accountMode')}</span>
              <span class="user-menu-item-hint">${this.escape(accountModeHint)}</span>
            </span>
          </a>
          <div class="user-menu-divider"></div>
          <div class="user-menu-section">
            <span class="user-menu-section-label">${t('language.label')}</span>
            ${localeOptions}
          </div>
          <div class="user-menu-divider"></div>
          ${installState.available ? `
            <button type="button" class="user-menu-item user-menu-install">
              <span class="user-menu-item-copy">
                <span class="user-menu-item-label">${t('userMenu.install')}</span>
                <span class="user-menu-item-hint">${t('userMenu.installHint')}</span>
              </span>
            </button>
            <div class="user-menu-divider"></div>
          ` : ''}
          <a href="/app/help/" class="user-menu-item">${t('nav.help')}</a>
          <div class="user-menu-divider"></div>
          <button type="button" class="user-menu-item user-menu-item--danger user-menu-logout">${t('auth.logout')}</button>
        </div>
      </div>
    `;

    this.querySelector('.user-menu-trigger')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleMenu();
    });

    this.querySelectorAll<HTMLElement>('[data-locale]').forEach(btn => {
      btn.addEventListener('click', () => {
        i18nService.setLocale(btn.dataset.locale as SupportedLocale);
        this.closeMenu();
      });
    });

    this.querySelector('.user-menu-install')?.addEventListener('click', async () => {
      this.closeMenu();
      await pwaInstallService.install();
    });

    this.querySelector('.user-menu-logout')?.addEventListener('click', () => {
      authService.logout();
    });
  }

  private openMenu(): void {
    const dropdown = this.querySelector<HTMLElement>('.user-menu-dropdown');
    const trigger = this.querySelector<HTMLElement>('.user-menu-trigger');
    if (!dropdown || !trigger) return;
    dropdown.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
  }

  private toggleMenu(): void {
    const dropdown = this.querySelector<HTMLElement>('.user-menu-dropdown');
    const trigger = this.querySelector<HTMLElement>('.user-menu-trigger');
    if (!dropdown || !trigger) return;
    const isOpen = !dropdown.hidden;
    dropdown.hidden = isOpen;
    trigger.setAttribute('aria-expanded', String(!isOpen));
  }

  private closeMenu(): void {
    const dropdown = this.querySelector<HTMLElement>('.user-menu-dropdown');
    const trigger = this.querySelector<HTMLElement>('.user-menu-trigger');
    if (!dropdown || !trigger) return;
    dropdown.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  }

  private supportsHover(): boolean {
    if (typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(RcUserMenu.HOVER_MEDIA_QUERY).matches;
  }

  private getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }

  private escape(str: string): string {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }
}

if (!customElements.get('rc-user-menu')) {
  customElements.define('rc-user-menu', RcUserMenu);
}
